import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Search, Home, Users, CreditCard, Settings, LayoutDashboard, LogOut, Plus, Sparkles, Moon, ArrowRight, Gift, MessageCircle } from 'lucide-react';
import type { Property } from './PropertyManager';
import { api } from '../services/api';

type AppView = 'overview' | 'properties' | 'dashboard' | 'studio' | 'variants' | 'batch' | 'team' | 'profile' | 'subscription' | 'referrals' | 'feedback';

interface Props {
  open: boolean;
  onClose: () => void;
  onNavigate: (view: AppView) => void;
  onSelectProperty: (property: Property) => void;
  onLogout: () => void;
  /** D-07: pass app_role from claims (or fallback user.role) — no bare string comparisons in JSX */
  userRole: 'beheerder' | 'medewerker' | 'superadmin';
}

type Item = {
  id: string;
  kind: 'nav' | 'action' | 'property';
  icon: React.ReactNode;
  title: string;
  sub?: string;
  run: () => void;
};

type Group = { label: string; items: Item[] };

export const CommandPalette: React.FC<Props> = ({ open, onClose, onNavigate, onSelectProperty, onLogout, userRole }) => {
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const [properties, setProperties] = useState<Property[]>([]);
  const [loadingProps, setLoadingProps] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    setQuery('');
    setActive(0);
    setTimeout(() => inputRef.current?.focus(), 20);

    setLoadingProps(true);
    api.get<Property[]>('/api/properties')
      .then((data) => setProperties(Array.isArray(data) ? data : []))
      .catch(() => setProperties([]))
      .finally(() => setLoadingProps(false));
  }, [open]);

  const groups = useMemo<Group[]>(() => {
    const q = query.trim().toLowerCase();

    const nav: Item[] = [
      { id: 'nav-overview', kind: 'nav', icon: <LayoutDashboard size={14} />, title: 'Overzicht', sub: 'Dashboard', run: () => onNavigate('overview') },
      { id: 'nav-properties', kind: 'nav', icon: <Home size={14} />, title: 'Woningen', sub: 'Alle projecten', run: () => onNavigate('properties') },
      ...((userRole === 'beheerder' || userRole === 'superadmin') ? [{ id: 'nav-team', kind: 'nav' as const, icon: <Users size={14} />, title: 'Team', sub: 'Collega\'s en rollen', run: () => onNavigate('team') }] : []),
      { id: 'nav-profile', kind: 'nav', icon: <Settings size={14} />, title: 'Profiel', sub: 'Account instellingen', run: () => onNavigate('profile') },
      { id: 'nav-referrals', kind: 'nav', icon: <Gift size={14} />, title: 'Referrals', sub: 'Nodig collega\'s uit', run: () => onNavigate('referrals') },
      { id: 'nav-subscription', kind: 'nav', icon: <CreditCard size={14} />, title: 'Abonnement', sub: 'Plan en credits', run: () => onNavigate('subscription') },
      { id: 'nav-feedback', kind: 'nav', icon: <MessageCircle size={14} />, title: 'Feedback', sub: 'Deel ideeën of bugs', run: () => onNavigate('feedback') },
    ];

    const actions: Item[] = [
      { id: 'act-new-property', kind: 'action', icon: <Plus size={14} />, title: 'Nieuwe woning toevoegen', sub: 'Ga naar Woningen', run: () => onNavigate('properties') },
      { id: 'act-upgrade', kind: 'action', icon: <Sparkles size={14} />, title: 'Credits bijkopen', sub: 'Naar abonnement', run: () => onNavigate('subscription') },
      { id: 'act-logout', kind: 'action', icon: <LogOut size={14} />, title: 'Uitloggen', sub: 'Einde sessie', run: () => onLogout() },
    ];

    const propItems: Item[] = properties.map(p => ({
      id: `prop-${p.id}`,
      kind: 'property',
      icon: <Home size={14} />,
      title: p.address,
      sub: [p.city, p.render_count ? `${p.render_count} render${p.render_count === 1 ? '' : 's'}` : null].filter(Boolean).join(' · ') || 'Geen renders',
      run: () => onSelectProperty(p),
    }));

    const filter = (arr: Item[]) => !q ? arr : arr.filter(i => (i.title + ' ' + (i.sub ?? '')).toLowerCase().includes(q));

    const result: Group[] = [];
    const filteredNav = filter(nav);
    if (filteredNav.length) result.push({ label: 'Navigatie', items: filteredNav });
    const filteredActions = filter(actions);
    if (filteredActions.length) result.push({ label: 'Acties', items: filteredActions });
    const filteredProps = filter(propItems).slice(0, 8);
    if (filteredProps.length) result.push({ label: 'Woningen', items: filteredProps });

    return result;
  }, [query, properties, userRole, onNavigate, onSelectProperty, onLogout]);

  const flatItems = useMemo(() => groups.flatMap(g => g.items), [groups]);

  useEffect(() => {
    setActive(0);
  }, [query]);

  // STAGERY-43: Keep the active item comfortably in view, not just barely visible.
  // - Uses manual scroll math instead of `scrollIntoView({ block: 'nearest' })`
  //   so the active row always has at least one full row of breathing room
  //   above and below within the scroll container.
  // - Falls back to `scrollIntoView({ block: 'center' })` when the item isn't
  //   yet measurable (e.g. just-rendered group header pushes layout).
  useEffect(() => {
    if (!open || !listRef.current) return;
    const list = listRef.current;
    const el = list.querySelector<HTMLButtonElement>(`[data-idx="${active}"]`);
    if (!el) return;

    const listRect = list.getBoundingClientRect();
    const itemRect = el.getBoundingClientRect();
    const margin = itemRect.height; // keep one row of breathing room

    if (itemRect.top < listRect.top + margin) {
      list.scrollTop -= (listRect.top + margin) - itemRect.top;
    } else if (itemRect.bottom > listRect.bottom - margin) {
      list.scrollTop += itemRect.bottom - (listRect.bottom - margin);
    }
  }, [active, open, groups]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onClose(); }
      else if (e.key === 'ArrowDown') { e.preventDefault(); setActive(a => Math.min(a + 1, flatItems.length - 1)); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(a => Math.max(a - 1, 0)); }
      else if (e.key === 'Enter') {
        e.preventDefault();
        const item = flatItems[active];
        if (item) { onClose(); setTimeout(() => item.run(), 40); }
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [open, flatItems, active, onClose]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[600] flex items-start justify-center pt-[10vh] sm:pt-[14vh] px-5 pb-5 bg-ink/50 backdrop-blur-sm animate-in fade-in duration-150 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      {/* STAGERY-43: cap modal height to viewport so the result list never
          overflows the screen on small viewports / when the soft keyboard is up. */}
      <div className="w-full max-w-[620px] max-h-[calc(100vh-14vh-1rem)] sm:max-h-[calc(100vh-18vh)] flex flex-col bg-white rounded-2xl border border-black/8 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.35)] overflow-hidden animate-in zoom-in-95 duration-150">
        <div className="flex items-center gap-3 px-5 border-b border-black/8">
          <Search size={16} className="text-ink/40" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Zoek woningen, acties of ga naar…"
            className="flex-1 py-[18px] text-[15px] font-sans outline-none bg-transparent placeholder:text-ink/40"
            autoComplete="off"
          />
        </div>

        <div ref={listRef} className="flex-1 min-h-0 max-h-[360px] overflow-y-auto py-1.5">
          {flatItems.length === 0 ? (
            <div className="py-10 text-center text-sm text-ink/55">
              {loadingProps ? 'Bezig met laden…' : 'Geen resultaten'}
            </div>
          ) : (
            groups.map((g, gi) => {
              let localIdx = 0;
              let base = 0;
              for (let i = 0; i < gi; i++) base += groups[i].items.length;
              return (
                <div key={g.label}>
                  <div className="px-5 pt-2 pb-1 text-[10px] font-semibold uppercase tracking-[0.12em] text-ink/55">{g.label}</div>
                  {g.items.map((item) => {
                    const absIdx = base + localIdx;
                    localIdx++;
                    const isActive = absIdx === active;
                    return (
                      <button
                        key={item.id}
                        data-idx={absIdx}
                        onClick={() => { onClose(); setTimeout(() => item.run(), 40); }}
                        onMouseEnter={() => setActive(absIdx)}
                        className={`w-full grid grid-cols-[26px_1fr_auto] items-center gap-2.5 px-5 py-2.5 text-left text-[13px] transition-colors ${isActive ? 'bg-accent/8' : 'hover:bg-black/3'}`}
                      >
                        <span className={`w-6 h-6 rounded-md grid place-items-center ${isActive ? 'bg-accent text-white' : 'bg-black/5 text-ink/55'}`}>
                          {item.icon}
                        </span>
                        <span className="min-w-0">
                          <span className="block font-semibold text-ink truncate tracking-[-0.005em]">{item.title}</span>
                          {item.sub && <span className="block text-[11px] text-ink/55 truncate mt-[1px]">{item.sub}</span>}
                        </span>
                        <span className={`text-[10px] uppercase tracking-[0.08em] font-semibold ${isActive ? 'text-accent' : 'text-ink/40'}`}>
                          {isActive ? <ArrowRight size={12} /> : item.kind}
                        </span>
                      </button>
                    );
                  })}
                </div>
              );
            })
          )}
        </div>

        <div className="flex items-center justify-between px-5 py-2 border-t border-black/8 bg-black/[0.02] text-[10.5px] font-medium text-ink/55">
          <span className="flex items-center gap-1.5">
            <kbd className="bg-white border border-black/8 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">↑↓</kbd> navigeer
            <span className="mx-1">·</span>
            <kbd className="bg-white border border-black/8 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">↵</kbd> open
            <span className="mx-1">·</span>
            <kbd className="bg-white border border-black/8 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">esc</kbd> sluit
          </span>
          <span className="flex items-center gap-1">
            <kbd className="bg-white border border-black/8 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">⌘</kbd>
            <kbd className="bg-white border border-black/8 rounded px-1.5 py-[1px] font-sans text-[10px] font-semibold">K</kbd>
          </span>
        </div>
      </div>
    </div>
  );
};
